import { Delete, ExportIcon } from "components/atoms/Icon";
import { Paragraph } from "components/atoms/Typography";
import DangerousModal from "components/molecules/DangerousModal";
import CustomPlanMenu from "components/organisms/Menu/CustomPlanMenu/CustomPlanMenu";
import SearchBar from "components/organisms/SearchBar/SearchBar";
import { useCustomPlanUpdate } from "hooks/CustomPlan/useCustomPlanUpdate";
import { useBuildingModelUid } from "hooks/useBuildingModelUid";
import posthog from "posthog-js";
import { useState } from "react";
import CsvDownloadButton from "react-json-to-csv";
import useCustomPlanSidebarState from "recoilStore/CustomPlan/useCustomPlanSidebarState";
import CarbonReductionPlanCsvExporter from "pages/BuildingPage/Helper/CarbonReductionPlanCsvExporter";
import { TABLE_IDS } from "utils/constants";
import AddMeasureButton from "./AddMeasureButton";
import { buildDeleteProjectModalOptions } from "./CustomPlanHeader.utils";
import EditCustomPlanButton from "./EditCustomPlanButton";
import styles from "./HeaderSharedStyles.module.scss";
import ReorderMeasuresButton from "./ReorderMeasuresButton";

interface CustomPlanHeaderProps {
    grossFloorArea: number;
    buildingModelCarbonReductionPlan: BuildingModelCarbonReductionPlan;
    setReorderModalIsOpen: () => void;
}

function CustomPlanHeader({
    grossFloorArea,
    buildingModelCarbonReductionPlan,
    setReorderModalIsOpen,
}: CustomPlanHeaderProps) {
    const [deleteProjectModalIsOpen, setDeleteProjectModalIsOpen] = useState(false);

    const {
        state: { selectedRows },
        setState: setCustomPlanSidebarState,
    } = useCustomPlanSidebarState();

    const buildingModelUid = useBuildingModelUid();

    // Hooks
    const { updateCustomPlanMeasures, isLoading: updateCustomPlanMeasuresLoading } = useCustomPlanUpdate({
        buildingModelUid,
        buildingModelCarbonReductionPlanId: buildingModelCarbonReductionPlan.id,
    });

    const exportData = new CarbonReductionPlanCsvExporter(
        buildingModelCarbonReductionPlan.plan,
        grossFloorArea
    ).toCsvRows();

    const handleDeleteCRM = () => {
        posthog.capture("Delete measure");

        updateCustomPlanMeasures("delete", {
            selectedRows,
            onSuccess: () => {
                setCustomPlanSidebarState((state) => ({
                    ...state,
                    selectedRows: [],
                }));
                setDeleteProjectModalIsOpen(false);
            },
        });
    };

    const deleteProjectsModalOptions = buildDeleteProjectModalOptions(
        selectedRows.length,
        updateCustomPlanMeasuresLoading
    );

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                marginLeft: "5px",
            }}
        >
            <SearchBar tableId={TABLE_IDS.CUSTOM_PLAN} />

            <div className={styles.customPlanHeader}>
                {selectedRows.length > 0 && (
                    <>
                        <EditCustomPlanButton selectedCustomPlanLength={selectedRows.length} />
                        {/* // Delete button */}
                        <Paragraph style={{ color: "var(--audette-content-negative)" }}>
                            <button
                                className={styles.deleteCustomPlanButton}
                                type="button"
                                onClick={() => setDeleteProjectModalIsOpen(true)}
                            >
                                <Delete />
                                Delete ({selectedRows.length})
                            </button>
                        </Paragraph>
                    </>
                )}

                {/* Modal to delete measure */}
                <DangerousModal
                    open={deleteProjectModalIsOpen}
                    onCancel={() => setDeleteProjectModalIsOpen(false)}
                    onDelete={handleDeleteCRM}
                    options={deleteProjectsModalOptions}
                    styleType="primary-negative"
                    loading={updateCustomPlanMeasuresLoading}
                />

                <ReorderMeasuresButton setReorderModalIsOpen={setReorderModalIsOpen} />
                <AddMeasureButton />
                <CsvDownloadButton data={exportData} filename="buildingProjectsData.csv" delimiter=",">
                    <div className="measures-page-flex-container">
                        <ExportIcon />
                        <Paragraph>Export</Paragraph>
                    </div>
                </CsvDownloadButton>

                <CustomPlanMenu currentCarbonReductionPlanUid={buildingModelCarbonReductionPlan.id} />
            </div>
        </div>
    );
}

export default CustomPlanHeader;
